import React from 'react'
import {
    WemoloButton,
    WemoloCrossIcon,
    WemoloDropdown,
    WemoloInput,
    WemoloMenuOption,
    WemoloTypography
} from '../../../../components/Wemolo'
import { DETECTION_EXPLORER_TEXT, DetectionExplorerFilterDefinition } from '../../config'
import { CategoryFilterDropdown } from './CategoryFilterDropdown'
import styles from './ActiveFilters.module.css'

// Synthetic option prepended to every multiSelect to toggle all real options at once. The value is
// namespaced so it can't collide with a real option value (categories, device ids, countries, …).
const SELECT_ALL_VALUE = '__select_all__'

export type FilterChipUIProps = {
    definition: DetectionExplorerFilterDefinition
    /** Options for the multiSelect dropdown; usually the definition's, but injected for dynamic filters (device). */
    multiSelectOptions: WemoloMenuOption[]
    textValue: string
    multiSelectValue: string[]
    rangeMin: string
    rangeMax: string
    onTextChange: (value: string) => void
    onMultiSelectChange: (value: string[]) => void
    onRangeMinChange: (value: string) => void
    onRangeMaxChange: (value: string) => void
    onRemove: () => void
}

export const FilterChipUI: React.FC<FilterChipUIProps> = ({
    definition,
    multiSelectOptions,
    textValue,
    multiSelectValue,
    rangeMin,
    rangeMax,
    onTextChange,
    onMultiSelectChange,
    onRangeMinChange,
    onRangeMaxChange,
    onRemove
}) => {
    const { input } = definition

    // "Select all" row pinned to the top of the menu. It reads as checked once every real option is
    // selected (so it carries into the value, and the trigger shows it — truncated by CSS). Clicking
    // it toggles the whole set; individual option toggles pass straight through.
    const allValues = multiSelectOptions.map((option) => option.value)
    const allSelected = allValues.length > 0 && allValues.every((value) => multiSelectValue.includes(value))
    const multiSelectOptionsWithSelectAll: WemoloMenuOption[] = [
        { value: SELECT_ALL_VALUE, label: DETECTION_EXPLORER_TEXT.filters.selectAll },
        ...multiSelectOptions
    ]
    const multiSelectValueWithSelectAll = allSelected ? [SELECT_ALL_VALUE, ...multiSelectValue] : multiSelectValue
    const handleMultiSelectChange = (next: string[]) => {
        const nextHasSelectAll = next.includes(SELECT_ALL_VALUE)
        if (nextHasSelectAll && !allSelected) return onMultiSelectChange(allValues)
        if (!nextHasSelectAll && allSelected) return onMultiSelectChange([])
        onMultiSelectChange(next.filter((value) => value !== SELECT_ALL_VALUE))
    }

    return (
        <div className={styles.chip}>
            <WemoloTypography variant="label-xsmall" tag="span" className={styles.label}>
                {definition.label}
            </WemoloTypography>
            <WemoloTypography variant="label-xsmall" tag="span" className={styles.operator}>
                {definition.operator}
            </WemoloTypography>

            {input.type === 'singleText' && (
                <WemoloInput
                    type="text"
                    value={textValue}
                    onChange={(e) => onTextChange(e.target.value)}
                    className={styles.valueInput}
                    aria-label={definition.label}
                />
            )}

            {input.type === 'multiSelect' && definition.key === 'category' && (
                <CategoryFilterDropdown value={multiSelectValue} onChange={onMultiSelectChange} />
            )}

            {input.type === 'multiSelect' && definition.key !== 'category' && (
                <WemoloDropdown
                    allowMultiple
                    options={multiSelectOptionsWithSelectAll}
                    value={multiSelectValueWithSelectAll}
                    onChange={(value) => handleMultiSelectChange(value as string[])}
                    placeholder={DETECTION_EXPLORER_TEXT.filters.selectValues}
                    className={styles.dropdown}
                />
            )}

            {input.type === 'numberRange' && (
                <span className={styles.rangeWrapper}>
                    <WemoloInput
                        type="number"
                        value={rangeMin}
                        onChange={(e) => onRangeMinChange(e.target.value)}
                        className={styles.valueInput}
                        placeholder="min"
                        aria-label={`${definition.label} min`}
                    />
                    <WemoloTypography variant="label-xsmall" tag="span" className={styles.rangeSeparator}>
                        –
                    </WemoloTypography>
                    <WemoloInput
                        type="number"
                        value={rangeMax}
                        onChange={(e) => onRangeMaxChange(e.target.value)}
                        className={styles.valueInput}
                        placeholder="max"
                        aria-label={`${definition.label} max`}
                    />
                </span>
            )}

            <WemoloButton
                view="ghost"
                size="small"
                icon={<WemoloCrossIcon />}
                onClick={onRemove}
                aria-label={`${DETECTION_EXPLORER_TEXT.filters.removeFilter}: ${definition.label}`}
                className={styles.removeButton}
            />
        </div>
    )
}
